import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spinner } from "react-bootstrap";
import {
  Megaphone, Webhook, Copy, RefreshCw, Save, Eye, EyeOff,
  CheckCircle2, XCircle, AlertTriangle, RotateCcw, Info, Shield,
  Facebook, Link2, ChevronDown, ChevronRight,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { startMetaBusinessConnect } from "../../utils/metaBusinessConnect";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, ConfirmModal, SelectControl,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import MetaLeadFormMappingModal from "./MetaLeadFormMappingModal";
import "./settings.scss";

/**
 * Live state of the Meta Pages grant, and the one place the owner can fix it.
 *
 * Driven by `/lead-sources/meta-ads/health`, which asks Meta what the token
 * carries *now* rather than trusting the scope list recorded when it was
 * granted. The two diverge whenever someone removes a permission in Business
 * Settings, and that divergence is invisible until leads stop arriving — so the
 * whole point of this panel is to make it loud and one click away from fixed.
 *
 * Silent when everything is healthy. A green banner restating "all good" on
 * every visit is noise, and noise is what gets an amber one ignored.
 */
const MetaHealthPanel = ({ health, connecting, onConnect }) => {
  if (!health || health.state === "healthy") return null;

  const COPY = {
    not_connected: {
      tone: "info",
      Icon: Link2,
      title: "Facebook Pages are not connected",
      body: "Lead forms from Facebook and Instagram will not reach your enquiries until you authorise access to your Pages.",
      cta: "Connect Facebook Pages",
    },
    needs_pages: {
      tone: "warning",
      Icon: AlertTriangle,
      title: "This authorisation cannot see your Facebook Pages",
      body: "Meta was connected, but without permission to list your Pages. Reconnect and allow the Pages permissions.",
      cta: "Reconnect Facebook",
    },
    degraded: {
      tone: "warning",
      Icon: AlertTriangle,
      title: "Some Facebook permissions are missing",
      body: "Your Pages are visible, but lead capture needs permissions that are no longer granted:",
      cta: "Reconnect Facebook",
    },
    unknown: {
      tone: "muted",
      Icon: Info,
      title: "Could not verify the Facebook connection",
      body: health.message || "Meta did not respond. This usually clears on its own — existing Pages keep working.",
      cta: null,
    },
  }[health.state];

  if (!COPY) return null;
  const { tone, Icon, title, body, cta } = COPY;

  return (
    <div className={`lead-health lead-health--${tone}`}>
      <Icon size={16} className="lead-health__icon" />
      <div className="lead-health__body">
        <div className="lead-health__title">{title}</div>
        <p className="lead-health__text">{body}</p>

        {/*
          Plain-language labels, not scope strings. The person fixing this is an
          administrator clicking through Meta's dialog, and `pages_manage_metadata`
          tells them nothing about what stopped working.
        */}
        {health.missingLabels?.length > 0 && (
          <ul className="lead-health__missing">
            {health.missingLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        )}

        {cta && (
          <AppButton size="sm" variant="default" onClick={onConnect} disabled={connecting}>
            <Facebook size={14} /> {connecting ? "Opening…" : cta}
          </AppButton>
        )}
      </div>
    </div>
  );
};

/**
 * Lead Sources Settings — inbound lead capture configuration.
 *   • Google Ads — tenant-scoped webhook URL + signing secret (paste into Google Ads).
 *   • Meta Lead Ads — Facebook/Instagram Lead Forms. Pages come from a dedicated
 *     Meta grant (see MetaHealthPanel), then are managed/re-synced here.
 */
const LeadSourcesSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(""); // shown once after rotate
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const { branches } = useBranch();
  const [admins, setAdmins] = useState([]);

  // Google Ads config from server
  const [config, setConfig] = useState({
    enabled: false,
    webhookUrl: "",
    tenantToken: "",
    googleKey: "",
    secretSet: false,
    defaultBranchId: "",
    assignment: { strategy: "round_robin", fixedAssigneeId: "" },
    lastReceivedAt: null,
    totalReceived: 0,
  });

  // Meta (Facebook/Instagram) Lead Ads config from server
  const [metaConfig, setMetaConfig] = useState({
    enabled: false,
    defaultBranchId: "",
    assignment: { strategy: "round_robin", fixedAssigneeId: "" },
    lastReceivedAt: null,
    totalReceived: 0,
    pages: [],
  });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaSyncing, setMetaSyncing] = useState(false);

  /**
   * What the Meta grant carries *right now*, from `/meta-ads/health`.
   *
   * Kept apart from `metaConfig` because they answer different questions and
   * fail differently: the config is our own database and always resolves, while
   * this is a live call to Meta that can be slow or unreachable. Folding them
   * into one request would mean a Graph timeout blanks the whole card.
   */
  const [metaHealth, setMetaHealth] = useState(null);
  const [metaConnecting, setMetaConnecting] = useState(false);
  const popupTimerRef = useRef(null);

  // Lead forms, nested under their Page. Collapsed by default — a Page can
  // carry a dozen archived forms and the Pages themselves are the headline.
  const [expandedPages, setExpandedPages] = useState(() => new Set());
  const [mappingForm, setMappingForm] = useState(null);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [formSavingId, setFormSavingId] = useState(null);
  const [mappingTargets, setMappingTargets] = useState([]);

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState("");
  const [logsSourceFilter, setLogsSourceFilter] = useState("");

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // ── Load config ──────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/lead-sources/google-ads");
      setConfig({
        enabled: !!data.enabled,
        webhookUrl: data.webhookUrl || "",
        tenantToken: data.tenantToken || "",
        googleKey: data.googleKey || "",
        secretSet: !!data.secretSet,
        defaultBranchId: data.defaultBranchId || "",
        assignment: {
          strategy: data?.assignment?.strategy || "round_robin",
          fixedAssigneeId: data?.assignment?.fixedAssigneeId || "",
        },
        lastReceivedAt: data.lastReceivedAt || null,
        totalReceived: data.totalReceived || 0,
      });
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to load configuration", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load Meta Lead Ads config ────────────────────────────────────────────
  const loadMetaConfig = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/lead-sources/meta-ads");
      setMetaConfig({
        enabled: !!data.enabled,
        defaultBranchId: data.defaultBranchId || "",
        assignment: {
          strategy: data?.assignment?.strategy || "round_robin",
          fixedAssigneeId: data?.assignment?.fixedAssigneeId || "",
        },
        lastReceivedAt: data.lastReceivedAt || null,
        totalReceived: data.totalReceived || 0,
        pages: data.pages || [],
      });
    } catch {
      /* silent — card shows the not-connected state */
    }
  }, []);

  // ── Load admins for assignee dropdown ────────────────────────────────────
  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admins/list");
      setAdmins(data.admins || data || []);
    } catch {
      /* silent — non-critical */
    }
  }, []);

  // ── Load ingestion logs ──────────────────────────────────────────────────
  const loadLogs = useCallback(async (page = 1, status = "", source = "") => {
    setLogsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (source) params.source = source;
      if (status) params.status = status;
      const { data } = await apiClient.get("/lead-sources/logs", { params });
      setLogs(data.logs || []);
      setLogsTotalPages(data.totalPages || 1);
      setLogsPage(page);
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to load logs", "error");
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // ── Meta: live connection health ─────────────────────────────────────────
  const loadMetaHealth = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/lead-sources/meta-ads/health");
      setMetaHealth(data);
    } catch {
      // Deliberately silent. Health is advisory — the card below still renders
      // the Pages we already know about, and a failed probe must not raise a
      // toast every time Settings is opened.
      setMetaHealth(null);
    }
  }, []);

  // Fetched once and passed down so the modal never keeps its own copy of the
  // server's accepted target list — two lists is how one goes stale.
  const loadMappingTargets = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/lead-sources/meta-ads/mapping-targets");
      setMappingTargets(data.targets || []);
    } catch {
      setMappingTargets([]); // modal falls back to its own labels
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadMetaConfig();
    loadMetaHealth();
    loadMappingTargets();
    loadAdmins();
    loadLogs(1, "");
  }, [loadConfig, loadMetaConfig, loadMetaHealth, loadMappingTargets, loadAdmins, loadLogs]);

  // ── Save config ──────────────────────────────────────────────────────────
  const handleSave = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        defaultBranchId: config.defaultBranchId || null,
        assignment: {
          strategy: config.assignment.strategy,
          fixedAssigneeId:
            config.assignment.strategy === "fixed"
              ? config.assignment.fixedAssigneeId || null
              : null,
        },
        ...overrides,
      };
      const { data } = await apiClient.patch("/lead-sources/google-ads", payload);
      // If just enabled for the first time, server returns new tokens
      setConfig((c) => ({
        ...c,
        enabled: !!data.enabled,
        webhookUrl: data.webhookUrl || c.webhookUrl,
        tenantToken: data.tenantToken || c.tenantToken,
        googleKey: data.googleKey || c.googleKey,
        secretSet: !!data.secretSet || c.secretSet,
      }));
      // If a secret was generated for the first time, server returns it once
      if (data.secret) {
        setRevealedSecret(data.secret);
        setShowSecret(true);
      }
      showToast("Settings saved", "success");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle enable ────────────────────────────────────────────────────────
  const handleToggleEnabled = async () => {
    const next = !config.enabled;
    await handleSave({ enabled: next });
  };

  /**
   * The Pages grant — a second, separate Meta authorisation.
   *
   * It cannot be folded into the WhatsApp connect: with `META_CONFIG_ID` set
   * that dialog is Embedded Signup, whose token carries WhatsApp scopes only and
   * can never see a Facebook Page. So lead capture needs its own click, and this
   * is it. Mirrors `handleConnectBusiness` in WhatsAppDashboard.
   */
  const handleConnectPages = async () => {
    setMetaConnecting(true);
    try {
      /**
       * Reads its config from this screen's own endpoint, not the WhatsApp one.
       * `/whatsapp/connect/config` is gated on the WhatsApp permission matrix,
       * in which `admin` holds no `config` right — so an enquiries admin could
       * manage every lead form here and never reconnect the Page feeding them.
       */
      const popup = await startMetaBusinessConnect({ from: "lead-sources" });

      // The callback page has no channel back to its opener, so watch for the
      // window closing and re-read from the server then. Polling the flag is
      // the only cross-origin-safe signal available here.
      if (popupTimerRef.current) clearInterval(popupTimerRef.current);
      popupTimerRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;
          loadMetaHealth();
          loadMetaConfig();
        }
      }, 800);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Failed to load connect configuration.",
        "error",
      );
    } finally {
      setMetaConnecting(false);
    }
  };

  // Interval outlives the click, so it has to be torn down with the component.
  useEffect(() => () => {
    if (popupTimerRef.current) clearInterval(popupTimerRef.current);
  }, []);

  // ── Meta: lead forms ─────────────────────────────────────────────────────
  const togglePageExpanded = (pageId) =>
    setExpandedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });

  /**
   * Switch a form off without disconnecting its Page.
   *
   * The case this exists for: an institute runs one form that feeds an agency's
   * CRM alongside one that should feed us. Their leads still arrive and are
   * still logged — they just stop becoming enquiries.
   */
  const handleFormToggle = async (form, isActive) => {
    setFormSavingId(form.formId);
    try {
      await apiClient.patch(`/lead-sources/meta-ads/forms/${form.formId}`, { isActive });
      showToast(
        isActive
          ? `"${form.name || form.formId}" will now create enquiries`
          : `"${form.name || form.formId}" will be ignored`,
        "success"
      );
      loadMetaConfig();
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to update lead form", "error");
    } finally {
      setFormSavingId(null);
    }
  };

  /**
   * Import a form's past leads.
   *
   * Safe to run repeatedly — the ingestion fingerprint skips anything already
   * received live, which is why this is a plain button rather than a
   * confirmation dialog.
   */
  const handleBackfill = async (form) => {
    setFormSavingId(form.formId);
    try {
      const { data } = await apiClient.post(
        `/lead-sources/meta-ads/forms/${form.formId}/backfill`
      );
      showToast(data.message, data.queued ? "success" : "info");
      loadMetaConfig();
      loadLogs(1, logsFilter, logsSourceFilter);
    } catch (e) {
      showToast(e?.response?.data?.message || "Could not import past leads", "error");
    } finally {
      setFormSavingId(null);
    }
  };

  const handleSaveMapping = async (fieldMapping) => {
    if (!mappingForm) return;
    setMappingSaving(true);
    try {
      await apiClient.patch(`/lead-sources/meta-ads/forms/${mappingForm.formId}`, { fieldMapping });
      showToast("Field mapping saved", "success");
      setMappingForm(null);
      loadMetaConfig();
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to save mapping", "error");
    } finally {
      setMappingSaving(false);
    }
  };

  // ── Meta: save config ────────────────────────────────────────────────────
  const handleMetaSave = async () => {
    setMetaSaving(true);
    try {
      await apiClient.patch("/lead-sources/meta-ads", {
        enabled: metaConfig.enabled,
        defaultBranchId: metaConfig.defaultBranchId || null,
        assignment: {
          strategy: metaConfig.assignment.strategy,
          fixedAssigneeId:
            metaConfig.assignment.strategy === "fixed"
              ? metaConfig.assignment.fixedAssigneeId || null
              : null,
        },
      });
      showToast("Meta settings saved", "success");
      loadMetaConfig();
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to save", "error");
    } finally {
      setMetaSaving(false);
    }
  };

  // ── Meta: re-discover Pages + re-subscribe lead forms ────────────────────
  const handleMetaSyncPages = async () => {
    setMetaSyncing(true);
    try {
      const { data } = await apiClient.post("/lead-sources/meta-ads/sync-pages");
      showToast(data.message || "Pages synced", data.errors?.length ? "warning" : "success");
      loadMetaConfig();
      // A sync that returned nothing is usually a scope problem, and the health
      // panel is what names it — so re-probe rather than leaving a stale green.
      loadMetaHealth();
    } catch (e) {
      showToast(e?.response?.data?.message || "Page sync failed", "error");
    } finally {
      setMetaSyncing(false);
    }
  };

  // ── Rotate secret ────────────────────────────────────────────────────────
  const handleRotateSecret = async () => {
    setShowRotateConfirm(false);
    setRotating(true);
    try {
      const { data } = await apiClient.post("/lead-sources/google-ads/rotate-secret");
      setRevealedSecret(data.secret || "");
      setShowSecret(true);
      setConfig((c) => ({ ...c, secretSet: true }));
      showToast("Secret rotated. Copy it now — it will not be shown again.", "warning");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to rotate secret", "error");
    } finally {
      setRotating(false);
    }
  };

  // ── Retry log ────────────────────────────────────────────────────────────
  const handleRetryLog = async (logId) => {
    try {
      await apiClient.post(`/lead-sources/logs/${logId}/retry`);
      showToast("Re-queued for processing", "success");
      loadLogs(logsPage, logsFilter, logsSourceFilter);
    } catch (e) {
      showToast(e?.response?.data?.message || "Retry failed", "error");
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => showToast(`${label} copied to clipboard`, "success"),
      () => showToast("Copy failed — please copy manually", "error")
    );
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const statusBadge = (s) => {
    const map = {
      received:  { color: "#0ea5e9", icon: Info,           label: "Received"  },
      processed: { color: "#10b981", icon: CheckCircle2,   label: "Processed" },
      duplicate: { color: "#94a3b8", icon: AlertTriangle,  label: "Duplicate" },
      failed:    { color: "#ef4444", icon: XCircle,        label: "Failed"    },
      // Deliberately not converted — a provider test lead, or a form switched
      // off in the Pages table above. Amber, not red: nothing went wrong.
      skipped:   { color: "#f59e0b", icon: Info,           label: "Skipped"   },
    };
    const cfg = map[s] || map.received;
    const Icon = cfg.icon;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 12, fontSize: 12,
        background: `${cfg.color}1a`, color: cfg.color,
      }}>
        <Icon size={12} /> {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spinner animation="border" size="sm" /> &nbsp; Loading…
      </div>
    );
  }

  return (
    <>
      {/* ── Google Ads Card ─────────────────────────────────────────────── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Megaphone size={18} /> Google Ads — Lead Form Webhook
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Receive leads from Google Ads Lead Form Extension directly into your
            enquiries pipeline. Each institute has a unique webhook URL.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {/* Enable toggle */}
          <div className="settings-form-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <strong>Status:</strong>{" "}
              {config.enabled ? (
                <span style={{ color: "#10b981" }}><CheckCircle2 size={14} /> Enabled</span>
              ) : (
                <span style={{ color: "#94a3b8" }}><XCircle size={14} /> Disabled</span>
              )}
              {config.lastReceivedAt && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Last lead received: {fmtDate(config.lastReceivedAt)} &middot;
                  &nbsp;Total: {config.totalReceived}
                </div>
              )}
            </div>
            <AppButton onClick={handleToggleEnabled} disabled={saving}>
              {config.enabled ? "Disable" : "Enable"}
            </AppButton>
          </div>

          {/* Webhook URL */}
          {config.enabled && config.webhookUrl && (
            <>
              <div className="settings-field">
                <label>
                  <Webhook size={14} /> Webhook URL
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    (paste this into Google Ads → Linked accounts → Webhooks for leads)
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    readOnly
                    value={config.webhookUrl}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  <AppButton variant="secondary" onClick={() => copyToClipboard(config.webhookUrl, "Webhook URL")}>
                    <Copy size={14} /> Copy
                  </AppButton>
                </div>
              </div>

              {/* Google Key */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>
                  <Shield size={14} /> Key (for Google Ads "Key" field)
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    Google Ads echoes this back in the payload to verify the receiver
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    readOnly
                    value={config.googleKey}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  <AppButton variant="secondary" onClick={() => copyToClipboard(config.googleKey, "Key")}>
                    <Copy size={14} /> Copy
                  </AppButton>
                </div>
              </div>

              {/* Secret */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>
                  <Shield size={14} /> HMAC Signing Secret
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    Used by signing relays (optional). Header: <code>x-lms-signature: sha256=&lt;hex&gt;</code>
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type={showSecret ? "text" : "password"}
                    readOnly
                    value={revealedSecret || (config.secretSet ? "••••••••••••••••••••••••" : "")}
                    placeholder={config.secretSet ? "" : "Not generated"}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  {revealedSecret && (
                    <AppButton variant="secondary" onClick={() => setShowSecret((v) => !v)}>
                      {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                    </AppButton>
                  )}
                  {revealedSecret && (
                    <AppButton variant="secondary" onClick={() => copyToClipboard(revealedSecret, "Secret")}>
                      <Copy size={14} /> Copy
                    </AppButton>
                  )}
                  <AppButton variant="secondary" onClick={() => setShowRotateConfirm(true)} disabled={rotating}>
                    <RefreshCw size={14} /> Rotate
                  </AppButton>
                </div>
                {revealedSecret && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>
                    <AlertTriangle size={12} /> Copy this secret now — it will not be shown again after you leave this page.
                  </div>
                )}
              </div>

              {/* Default Branch */}
              <div className="settings-field" style={{ marginTop: 16 }}>
                <label>Default Branch (optional)</label>
                <select
                  value={config.defaultBranchId || ""}
                  onChange={(e) => setConfig((c) => ({ ...c, defaultBranchId: e.target.value }))}
                  style={{ width: "100%" }}
                >
                  <option value="">— Use first available —</option>
                  {(branches || []).map((b) => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {/* Assignment strategy */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>Auto-Assignment Strategy</label>
                <select
                  value={config.assignment.strategy}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      assignment: { ...c.assignment, strategy: e.target.value },
                    }))
                  }
                  style={{ width: "100%" }}
                >
                  <option value="round_robin">Round-robin among admins</option>
                  <option value="fixed">Fixed assignee</option>
                  <option value="none">No auto-assignment</option>
                </select>
              </div>

              {config.assignment.strategy === "fixed" && (
                <div className="settings-field" style={{ marginTop: 12 }}>
                  <label>Fixed Assignee</label>
                  <select
                    value={config.assignment.fixedAssigneeId || ""}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        assignment: { ...c.assignment, fixedAssigneeId: e.target.value },
                      }))
                    }
                    style={{ width: "100%" }}
                  >
                    <option value="">— Select admin —</option>
                    {admins.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.fullName || a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="settings-save-row" style={{ marginTop: 16 }}>
                <AppButton onClick={() => handleSave()} disabled={saving}>
                  <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
                </AppButton>
              </div>
            </>
          )}

          {!config.enabled && (
            <div style={{
              background: "#f1f5f9", padding: 16, borderRadius: 8,
              fontSize: 13, color: "#475569", marginTop: 12,
            }}>
              <Info size={14} /> &nbsp;
              Enable Google Ads above to generate your unique webhook URL and signing key.
              You'll then paste these into your Google Ads account.
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Setup Instructions ──────────────────────────────────────────── */}
      {config.enabled && (
        <AppCard style={{ marginTop: 16 }}>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Info size={18} /> Setup Instructions
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <ol style={{ paddingLeft: 20, lineHeight: 1.9, fontSize: 14 }}>
              <li>In Google Ads, open <strong>Tools &amp; Settings → Linked accounts → Webhooks for leads</strong>.</li>
              <li>Paste the <strong>Webhook URL</strong> above.</li>
              <li>Paste the <strong>Key</strong> above into Google's "Key" field.</li>
              <li>Click <strong>Send test data</strong> — a test entry should appear in the Ingestion Logs below.</li>
              <li>Link the webhook to your Lead Form extensions / assets.</li>
            </ol>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── Meta (Facebook & Instagram) Lead Forms Card ─────────────────── */}
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Facebook size={18} /> Meta — FB &amp; Instagram Lead Forms
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Leads from Facebook and Instagram Lead Ads flow straight into your enquiries.
            This is a separate authorisation from WhatsApp — connecting WhatsApp does not
            grant access to your Pages, and you do not need a WhatsApp account to connect
            it.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <MetaHealthPanel
            health={metaHealth}
            connecting={metaConnecting}
            onConnect={handleConnectPages}
          />

          <div className="settings-form-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <strong>Status:</strong>{" "}
              {metaConfig.enabled && metaConfig.pages.length > 0 ? (
                <span style={{ color: "#10b981" }}><CheckCircle2 size={14} /> Connected</span>
              ) : (
                <span style={{ color: "#94a3b8" }}><XCircle size={14} /> Not connected</span>
              )}
              {metaConfig.lastReceivedAt && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Last lead received: {fmtDate(metaConfig.lastReceivedAt)} &middot;
                  &nbsp;Total: {metaConfig.totalReceived}
                </div>
              )}

              {/* The live answer from Meta, not the snapshot taken when the
                  grant was made. After a re-authorisation this is the only
                  place that confirms a newly-requested permission actually
                  arrived — Meta drops ones it has not approved for the user
                  without saying so, and the dialog closes just as happily. */}
              {metaHealth?.granted?.length > 0 && (
                <div className="lead-scopes">
                  <span className="lead-scopes__label">Permissions Meta reports on this token:</span>
                  {metaHealth.granted.map((s) => (
                    <code key={s} className="lead-scopes__chip">{s}</code>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {/*
                Always offered, not only when something is broken.

                `MetaHealthPanel` above is deliberately silent while the
                connection is healthy — and a healthy connection is exactly the
                case that needs this: when the permissions we *ask* for change,
                the existing token keeps working and nothing anywhere reports a
                problem, so there is no error state to hang a button off. Without
                this the only way to pick up a new scope is to break the
                connection first.
              */}
              <AppButton
                variant="secondary"
                onClick={handleConnectPages}
                disabled={metaConnecting}
                title="Open Meta's permission dialog again — use this after new permissions are enabled"
              >
                <Link2 size={14} /> {metaConnecting ? "Opening…" : "Re-authorise"}
              </AppButton>
              <AppButton variant="secondary" onClick={handleMetaSyncPages} disabled={metaSyncing}>
                <RefreshCw size={14} /> {metaSyncing ? "Syncing…" : "Sync Pages"}
              </AppButton>
            </div>
          </div>

          {metaConfig.pages.length === 0 ? (
            <div className="lead-empty-note">
              <Info size={14} />
              <div>
                No Facebook Pages connected yet. Use <strong>Connect Facebook Pages</strong> above
                and allow the Pages and lead permissions — your Pages are then subscribed to their
                lead forms automatically.
                <div className="lead-empty-note__sub">
                  You need to be an admin of the Page in Meta Business Manager. Already connected
                  elsewhere? Click <strong>Sync Pages</strong>.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="lead-log-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th>Page</th>
                      <th>Category</th>
                      <th>Instagram</th>
                      <th>Webhook</th>
                      <th>Forms</th>
                      <th>Leads</th>
                      <th>Last Lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metaConfig.pages.map((p) => {
                      const forms = p.forms || [];
                      const open = expandedPages.has(p.pageId);
                      return (
                        <React.Fragment key={p.pageId}>
                          <tr>
                            <td>
                              {forms.length > 0 && (
                                <button
                                  type="button"
                                  className="mlf-expand"
                                  onClick={() => togglePageExpanded(p.pageId)}
                                  aria-label={open ? "Hide lead forms" : "Show lead forms"}
                                  aria-expanded={open}
                                >
                                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              )}
                            </td>
                            <td>
                              <Link2 size={12} /> {p.pageName || p.pageId}
                              <div className="mlf-id">{p.pageId}</div>
                            </td>
                            <td>{p.category || "—"}</td>
                            <td>{p.instagramBusinessAccountId ? <CheckCircle2 size={14} color="#10b981" /> : "—"}</td>
                            <td>
                              {p.leadgenSubscribed ? (
                                <span className="mlf-ok"><CheckCircle2 size={12} /> Subscribed</span>
                              ) : (
                                <span className="mlf-bad" title={p.subscriptionError || ""}>
                                  <AlertTriangle size={12} /> Not subscribed
                                </span>
                              )}
                            </td>
                            <td>
                              {forms.length === 0
                                ? <span className="mlf-muted">None found</span>
                                : `${forms.filter((f) => f.isActive).length} of ${forms.length} on`}
                            </td>
                            <td>{p.totalLeads || 0}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(p.lastLeadAt)}</td>
                          </tr>

                          {/*
                            Forms nested under their Page rather than listed
                            separately: a form id means nothing on its own, and
                            "which Page is this on" is the first thing anyone asks.
                          */}
                          {open && forms.map((f) => (
                            <tr className="mlf-form-row" key={f.formId}>
                              <td />
                              <td colSpan={7}>
                                <div className="mlf-form">
                                  <label className="mlf-form__toggle">
                                    <input
                                      type="checkbox"
                                      checked={!!f.isActive}
                                      disabled={formSavingId === f.formId}
                                      onChange={(e) => handleFormToggle(f, e.target.checked)}
                                    />
                                    <span />
                                  </label>

                                  <div className="mlf-form__main">
                                    <div className="mlf-form__name">
                                      {f.name || f.formId}
                                      {f.missingSince && (
                                        <span className="mlf-chip mlf-chip--warn">Archived in Meta</span>
                                      )}
                                      {!f.isActive && (
                                        <span className="mlf-chip">Ignored</span>
                                      )}
                                    </div>
                                    <div className="mlf-form__meta">
                                      {f.questions?.length || 0} question{f.questions?.length === 1 ? "" : "s"}
                                      {" · "}{f.totalLeads || 0} lead{f.totalLeads === 1 ? "" : "s"}
                                      {f.lastLeadAt ? ` · last ${fmtDate(f.lastLeadAt)}` : ""}
                                    </div>
                                  </div>

                                  {/* Only offered for forms that are actually
                                      converting — importing history into a
                                      switched-off form just fills the log with
                                      skipped rows. */}
                                  {f.isActive && (
                                    <AppButton
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleBackfill(f)}
                                      disabled={formSavingId === f.formId}
                                      title="Import leads from the last 90 days"
                                    >
                                      <RotateCcw size={13} /> Import past
                                    </AppButton>
                                  )}

                                  <AppButton
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setMappingForm(f)}
                                    disabled={!f.questions?.length}
                                  >
                                    <Shield size={13} /> Map fields
                                  </AppButton>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Default Branch */}
              <div className="settings-field" style={{ marginTop: 16 }}>
                <label>Default Branch (optional)</label>
                <select
                  value={metaConfig.defaultBranchId || ""}
                  onChange={(e) => setMetaConfig((c) => ({ ...c, defaultBranchId: e.target.value }))}
                  style={{ width: "100%" }}
                >
                  <option value="">— Use first available —</option>
                  {(branches || []).map((b) => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {/* Assignment strategy */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>Auto-Assignment Strategy</label>
                <select
                  value={metaConfig.assignment.strategy}
                  onChange={(e) =>
                    setMetaConfig((c) => ({
                      ...c,
                      assignment: { ...c.assignment, strategy: e.target.value },
                    }))
                  }
                  style={{ width: "100%" }}
                >
                  <option value="round_robin">Round-robin among admins</option>
                  <option value="fixed">Fixed assignee</option>
                  <option value="none">No auto-assignment</option>
                </select>
              </div>

              {metaConfig.assignment.strategy === "fixed" && (
                <div className="settings-field" style={{ marginTop: 12 }}>
                  <label>Fixed Assignee</label>
                  <select
                    value={metaConfig.assignment.fixedAssigneeId || ""}
                    onChange={(e) =>
                      setMetaConfig((c) => ({
                        ...c,
                        assignment: { ...c.assignment, fixedAssigneeId: e.target.value },
                      }))
                    }
                    style={{ width: "100%" }}
                  >
                    <option value="">— Select admin —</option>
                    {admins.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.fullName || a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="settings-save-row" style={{ marginTop: 16 }}>
                <AppButton onClick={handleMetaSave} disabled={metaSaving}>
                  <Save size={16} /> {metaSaving ? "Saving…" : "Save Changes"}
                </AppButton>
              </div>
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Ingestion Logs ──────────────────────────────────────────────── */}
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Webhook size={18} /> Ingestion Logs
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">Recent webhook deliveries from all configured lead sources</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="lead-log-filter-row">
            <SelectControl
              value={logsSourceFilter}
              ariaLabel="Filter by source"
              onChange={(val) => { setLogsSourceFilter(val); loadLogs(1, logsFilter, val); }}
              options={[
                { value: "", label: "All sources" },
                { value: "google_ads", label: "Google Ads" },
                { value: "meta_ads", label: "Meta Ads" },
              ]}
            />
            <SelectControl
              value={logsFilter}
              ariaLabel="Filter by status"
              onChange={(val) => { setLogsFilter(val); loadLogs(1, val, logsSourceFilter); }}
              options={[
                { value: "", label: "All statuses" },
                { value: "received", label: "Received" },
                { value: "processed", label: "Processed" },
                { value: "duplicate", label: "Duplicate" },
                { value: "failed", label: "Failed" },
                { value: "skipped", label: "Skipped" },
              ]}
            />
            <AppButton variant="secondary" onClick={() => loadLogs(logsPage, logsFilter, logsSourceFilter)} disabled={logsLoading}>
              <RefreshCw size={14} /> Refresh
            </AppButton>
          </div>

          {logsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" /></div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 14 }}>
              No ingestion logs yet
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="lead-log-table">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Signature</th>
                    <th>External ID</th>
                    <th>Enquiry</th>
                    <th>Error</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtDate(log.receivedAt)}</td>
                      <td>{log.source}</td>
                      <td>{statusBadge(log.status)}</td>
                      <td>
                        {log.signatureValid ? (
                          <CheckCircle2 size={14} color="var(--color-success, #10b981)" />
                        ) : (
                          <XCircle size={14} color="var(--color-danger, #ef4444)" />
                        )}
                      </td>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                        {log.externalLeadId || "—"}
                      </td>
                      <td>
                        {log.enquiryId ? (
                          <a href={`/enquiries/${log.enquiryId._id || log.enquiryId}`} className="lead-log-link">
                            View →
                          </a>
                        ) : "—"}
                      </td>
                      <td className="lead-log-error">
                        {log.errorMessage || "—"}
                      </td>
                      <td>
                        {/*
                          Skipped is retryable too: the common case is a form
                          switched off by mistake, or switched on after the fact.
                          Re-queueing re-runs the gate, so a still-disabled form
                          simply skips again.
                        */}
                        {(log.status === "failed" || log.status === "skipped") && (
                          <AppButton variant="secondary" size="sm" onClick={() => handleRetryLog(log._id)}>
                            <RotateCcw size={12} /> Retry
                          </AppButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {logsTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <AppButton variant="secondary" size="sm"
                disabled={logsPage <= 1}
                onClick={() => loadLogs(logsPage - 1, logsFilter, logsSourceFilter)}>
                Prev
              </AppButton>
              <span style={{ alignSelf: "center", fontSize: 13 }}>
                Page {logsPage} / {logsTotalPages}
              </span>
              <AppButton variant="secondary" size="sm"
                disabled={logsPage >= logsTotalPages}
                onClick={() => loadLogs(logsPage + 1, logsFilter, logsSourceFilter)}>
                Next
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Rotate Secret Confirm ───────────────────────────────────────── */}
      <ConfirmModal
        show={showRotateConfirm}
        title="Rotate Signing Secret?"
        message="Any signing relay using the current secret will stop working until updated with the new value. The new secret is shown only once."
        confirmText="Rotate"
        confirmVariant="danger"
        onHide={() => setShowRotateConfirm(false)}
        onConfirm={handleRotateSecret}
      />

      {/* ── Lead form field mapping ─────────────────────────────────────── */}
      <MetaLeadFormMappingModal
        show={!!mappingForm}
        form={mappingForm}
        targets={mappingTargets}
        saving={mappingSaving}
        onHide={() => setMappingForm(null)}
        onSave={handleSaveMapping}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </>
  );
};

export default LeadSourcesSettings;
